import { Modal } from 'bootstrap';
import { AxiosError } from 'axios';
import { addFavorite, removeFavorite } from '../../components/LifeplanDocs/Favorites/services';
import { FavoriteKind } from '../../components/LifeplanDocs/Favorites/types';

const FAVORITES_MAX_LIMIT_MODAL_ID = '#favorites-max-limit-modal';
const MAX_LIMIT_ERROR_STATUS = 400;

function showFavoriteLimitModal() {
  Modal.getOrCreateInstance(FAVORITES_MAX_LIMIT_MODAL_ID).show();
}

/**
 * Updates the button's state to reflect whether the item is marked as a favorite.
 *
 * @param button The button element that toggles the favorite state.
 * @param isFavorite `true` if marked as favorite, `false` otherwise.
 */
function updateFavoriteButton(button: HTMLButtonElement, isFavorite: boolean) {
  const icon = button.querySelector<HTMLSpanElement>('.docs-favorite-card__toggle-btn__icon');
  const loading = button.querySelector<HTMLSpanElement>('.docs-favorite-card__toggle-btn__loading');

  if (icon) {
    icon.setAttribute('data-favorite', String(isFavorite));
    button.setAttribute('data-favorite', String(isFavorite));
  }

  button.setAttribute('aria-label', isFavorite ? 'Eliminar de favoritos' : 'Agregar a favoritos');
  button.setAttribute('title', isFavorite ? 'Eliminar de favoritos' : 'Agregar a favoritos');

  if (loading) {
    loading.classList.add('d-none');
  }
  if (icon) {
    icon.classList.remove('d-none');
  }
}

/**
 * Shows loading state on the button.
 *
 * @param button The button element to show loading state on.
 */
function showLoadingState(button: HTMLButtonElement) {
  const icon = button.querySelector<HTMLSpanElement>('.docs-favorite-card__toggle-btn__icon');
  const loading = button.querySelector<HTMLSpanElement>('.docs-favorite-card__toggle-btn__loading');

  if (loading) {
    loading.classList.remove('d-none');
  }
  if (icon) {
    icon.classList.add('d-none');
  }

  button.setAttribute('disabled', 'true');
}

/**
 * Handles the favorite toggle click.
 *
 * @param button The button element to initialize.
 */
function initDocsFavoriteButton(button: HTMLButtonElement) {
  button.addEventListener('click', (event) => {
    event.preventDefault();
    const id = Number(button.dataset.id);
    const kind = button.dataset.kind as FavoriteKind;
    const isFavorite = button.dataset.favorite === 'true';
    // Favorites page specific attribute to remove the card from the list when unfavorited
    const isRemovable = button.closest('.docs-favorite-card')?.hasAttribute('data-removable');

    if (!id || !kind) {
      console.error('Missing required data attributes for favorite button');
      return;
    }

    showLoadingState(button);

    if (isFavorite) {
      removeFavorite(id, kind)
        .then(() => {
          updateFavoriteButton(button, false);
          if (isRemovable) {
            // Means that the card MUST be wrapped in a li element!
            button.closest('li')?.remove();
          }
        })
        .catch((error: AxiosError) => {
          console.error('Error al actualizar favorito:');

          if (error.status === MAX_LIMIT_ERROR_STATUS) {
            showFavoriteLimitModal();
          }

          updateFavoriteButton(button, isFavorite);
        })
        .finally(() => {
          button.removeAttribute('disabled');
        });
    } else {
      addFavorite(id, kind)
        .then(() => {
          updateFavoriteButton(button, true);
        })
        .catch((error: AxiosError) => {
          if (error.status === MAX_LIMIT_ERROR_STATUS) {
            showFavoriteLimitModal();
          }
          updateFavoriteButton(button, isFavorite);
        })
        .finally(() => {
          button.removeAttribute('disabled');
        });
    }
  });
}

window.addEventListener('DOMContentLoaded', () => {
  const docsFavoriteButtons = document.querySelectorAll<HTMLButtonElement>('[data-toggle="docs-favorite"]');
  docsFavoriteButtons.forEach(initDocsFavoriteButton);
});
